// ViewToolbarFacetMenu.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './ViewToolbarFacetMenu.css';

// Icons
import Icon from 'svg/Icon';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import Popup from 'components/popup/Popup';
import PopupSeparator from 'components/popup/separator/PopupSeparator';
import PopupSubmenuOption from 'components/popup/submenu/PopupSubmenuOption';
import ViewToolbarFilterPills from 'components/viewtoolbar/ViewToolbarFilterPills';

const normalizeValue = (value) => String(value || '').trim();

/**
 * Builds the compact trigger label for active lens selections.
 *
 * @param {Array} facetFields
 * @param {object} facetSelections
 * @param {string|null} boardFieldKey
 * @param {Array} boardFields
 * @param {boolean} isBoardMode
 * @returns {string}
 */
const buildFacetTriggerLabel = (
    facetFields,
    facetSelections,
    boardFieldKey,
    boardFields,
    isBoardMode,
) => {
    const fields = Array.isArray(facetFields) ? facetFields : [];
    const activeFields = fields
        .map((field) => {
            const selectedValue = normalizeValue(facetSelections?.[field.key]);
            if (!selectedValue) return null;

            const selectedOption = (Array.isArray(field.options) ? field.options : []).find((option) => {
                return normalizeValue(option?.value) === selectedValue;
            });

            return {
                fieldLabel: String(field.label || field.key || '').trim(),
                valueLabel: String(selectedOption?.label || selectedValue).trim() || selectedValue,
            };
        })
        .filter(Boolean);

    if (isBoardMode) {
        const boardLabel = String(
            (Array.isArray(boardFields) ? boardFields : [])
                .find((field) => field?.key === boardFieldKey)?.label
            || boardFieldKey
            || '',
        ).trim();

        if (boardLabel && activeFields.length === 1) {
            return `${boardLabel} · ${activeFields[0].valueLabel}`;
        }
        if (boardLabel && activeFields.length > 1) {
            return `${boardLabel} · ${activeFields.length} filters`;
        }
        if (boardLabel) return `Group by ${boardLabel}`;
    }

    if (!activeFields.length) return 'Filters';
    if (activeFields.length === 1) return activeFields[0].valueLabel;
    return `${activeFields.length} filters`;
};

/**
 * ViewToolbarFacetMenu
 *
 * Lens control: filter by enum/multiselect/tags, and on Board also choose Group by.
 *
 * @param {Array} facetFields - Schema-backed lens field definitions.
 * @param {object} facetSelections - Selected value by field key.
 * @param {function} onChange - Called with field key and next value.
 * @param {boolean} showLabel - Whether to show the trigger label beside the icon.
 * @param {boolean} [isBoardMode=false] - Whether Board group-by section is shown.
 * @param {Array} [boardFields=[]] - Board-capable fields for Group by.
 * @param {string|null} [boardFieldKey=null] - Active Board group field.
 * @param {function} [onBoardFieldChange] - Called when Group by changes.
 *
 * @returns {ReactNode|null} The rendered facet menu or null when nothing to show.
 */
const ViewToolbarFacetMenu = React.memo(({
    facetFields = [],
    facetSelections = {},
    onChange,
    showLabel = false,
    isBoardMode = false,
    boardFields = [],
    boardFieldKey = null,
    onBoardFieldChange,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- REFS ----- //

    const anchorRef = useRef(null);

    // ----- STATE ----- //

    const [popupOpen, setPopupOpen] = useState(false);

    // ----- MEMOS ----- //

    const groupFields = useMemo(() => (
        Array.isArray(boardFields) ? boardFields.filter((field) => field?.key) : []
    ), [boardFields]);

    const filterFields = useMemo(() => {
        const fields = Array.isArray(facetFields) ? facetFields.filter((field) => field?.key) : [];
        if (!isBoardMode || !boardFieldKey) return fields;
        return fields.filter((field) => field.key !== boardFieldKey);
    }, [boardFieldKey, facetFields, isBoardMode]);

    const triggerLabel = useMemo(() => (
        buildFacetTriggerLabel(
            filterFields,
            facetSelections,
            boardFieldKey,
            groupFields,
            isBoardMode,
        )
    ), [boardFieldKey, facetSelections, filterFields, groupFields, isBoardMode]);

    const hasActiveSelections = useMemo(() => (
        filterFields.some((field) => normalizeValue(facetSelections?.[field.key]))
    ), [facetSelections, filterFields]);

    const hasLensContent = isBoardMode
        ? (groupFields.length > 0 || filterFields.length > 0)
        : filterFields.length > 0;

    const popupButtonTitle = triggerLabel || (isBoardMode ? 'Board options' : 'Filters');
    const showPills = !isMobile && hasActiveSelections;
    const showTriggerLabel = showLabel && !showPills;

    // ----- HANDLERS ----- //

    /**
     * handleSelect
     *
     * Handles a filter selection change.
     *
     * @param {string} fieldKey
     * @param {string} nextValue
     */
    const handleSelect = (fieldKey, nextValue) => {
        onChange?.(fieldKey, nextValue);
        setPopupOpen(false);
    };

    /**
     * handleGroupBySelect
     *
     * Handles a Board group-by selection.
     *
     * @param {string} nextFieldKey
     */
    const handleGroupBySelect = (nextFieldKey) => {
        onBoardFieldChange?.(nextFieldKey);
        setPopupOpen(false);
    };

    /**
     * handleClearAll
     *
     * Clears all active filter selections.
     */
    const handleClearAll = () => {
        filterFields.forEach((field) => {
            if (normalizeValue(facetSelections?.[field.key])) {
                onChange?.(field.key, '');
            }
        });
        setPopupOpen(false);
    };

    /**
     * handleOpenMenu
     *
     * Opens the lens popup from the trigger or a filter pill.
     */
    const handleOpenMenu = () => {
        setPopupOpen(true);
    };

    /**
     * handleClearPill
     *
     * Clears one active facet from a desktop filter pill.
     *
     * @param {string} fieldKey
     */
    const handleClearPill = (fieldKey) => {
        onChange?.(fieldKey, '');
    };

    if (!hasLensContent) return null;

    // ----- RENDER ----- //

    return (
        <>
            <div
                className="view-toolbar-facet-menu-container"
                ref={anchorRef}
            >

                {/* TRIGGER */}
                <div
                    className="view-toolbar-facet-menu-trigger"
                    style={{
                        color: (hasActiveSelections || (isBoardMode && boardFieldKey))
                            ? theme.foreground
                            : theme.foregroundMuted,
                    }}
                    title={popupButtonTitle}
                    onClick={() => setPopupOpen((prev) => !prev)}
                >
                    <Icon
                        iconKey="tune"
                        width={20}
                        height={20}
                        color={(hasActiveSelections || (isBoardMode && boardFieldKey))
                            ? theme.foreground
                            : theme.foregroundMuted}
                    />

                    {/* TRIGGER TEXT */}
                    {showTriggerLabel ? (
                        <div className="view-toolbar-facet-menu-trigger-text">
                            {triggerLabel}
                        </div>
                    ) : null}

                    {showPills ? null : (
                        <Icon
                            iconKey="keyboard_arrow_down"
                            width={18}
                            height={18}
                            color={theme.foregroundMuted}
                        />
                    )}
                </div>

                {showPills ? (
                    <ViewToolbarFilterPills
                        facetFields={filterFields}
                        facetSelections={facetSelections}
                        isBoardMode={isBoardMode}
                        boardFieldKey={boardFieldKey}
                        onClear={handleClearPill}
                        onOpenMenu={handleOpenMenu}
                    />
                ) : null}
            </div>

            <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>

                {/* POPUP */}
                <div
                    className="view-toolbar-facet-menu-popup"
                    style={{
                        maxHeight: '560px',
                        maxWidth: '260px',
                    }}
                >
                    {isBoardMode && groupFields.length ? (
                        <>
                            <PopupSubmenuOption
                                label="Group by"
                                note={String(
                                    groupFields.find((field) => field.key === boardFieldKey)?.label
                                    || boardFieldKey
                                    || 'Select field',
                                ).trim()}
                                selected={Boolean(boardFieldKey)}
                            >
                                {groupFields.map((field) => {
                                    const fieldKey = String(field.key || '').trim();
                                    const fieldLabel = String(field.label || fieldKey).trim() || fieldKey;
                                    const isSelected = fieldKey === boardFieldKey;

                                    return (
                                        <PopupOption
                                            key={`group:${fieldKey}`}
                                            label={fieldLabel}
                                            rightContent={isSelected ? (
                                                <Icon
                                                    iconKey="check"
                                                    width={16}
                                                    height={16}
                                                    color={theme.foregroundMuted}
                                                />
                                            ) : null}
                                            onClick={() => handleGroupBySelect(fieldKey)}
                                        />
                                    );
                                })}
                            </PopupSubmenuOption>

                            {filterFields.length ? <PopupSeparator /> : null}
                        </>
                    ) : null}

                    {filterFields.map((field) => {
                        const fieldKey = String(field.key || '').trim();
                        const fieldLabel = String(field.label || fieldKey).trim() || fieldKey;
                        const selectedValue = normalizeValue(facetSelections?.[fieldKey]);
                        const selectedOption = (Array.isArray(field.options) ? field.options : []).find((option) => {
                            return normalizeValue(option?.value) === selectedValue;
                        });
                        const clearOptionLabel = fieldLabel ? `Any ${fieldLabel}` : 'Any';
                        const submenuNote = selectedOption?.label || (selectedValue ? selectedValue : clearOptionLabel);

                        return (
                            <PopupSubmenuOption
                                key={fieldKey}
                                label={fieldLabel}
                                note={submenuNote}
                                selected={Boolean(selectedValue)}
                            >
                                <PopupOption
                                    label={clearOptionLabel}
                                    labelColor={theme.foregroundMuted}
                                    rightContent={selectedValue ? null : (
                                        <Icon
                                            iconKey="check"
                                            width={16}
                                            height={16}
                                            color={theme.foregroundMuted}
                                        />
                                    )}
                                    onClick={() => handleSelect(fieldKey, '')}
                                />

                                <PopupSeparator />

                                {(Array.isArray(field.options) ? field.options : []).map((option) => {
                                    const optionValue = normalizeValue(option?.value);
                                    const optionLabel = String(option?.label || optionValue).trim() || optionValue;
                                    const isSelected = optionValue === selectedValue;

                                    return (
                                        <PopupOption
                                            key={`${fieldKey}:${optionValue}`}
                                            label={optionLabel}
                                            rightContent={isSelected ? (
                                                <Icon
                                                    iconKey="check"
                                                    width={16}
                                                    height={16}
                                                    color={theme.foregroundMuted}
                                                />
                                            ) : null}
                                            onClick={() => handleSelect(fieldKey, optionValue)}
                                        />
                                    );
                                })}
                            </PopupSubmenuOption>
                        );
                    })}

                    {hasActiveSelections ? (
                        <>
                            <PopupSeparator />
                            <PopupOption
                                label="Clear all filters"
                                labelColor={theme.foregroundMuted}
                                onClick={handleClearAll}
                            />
                        </>
                    ) : null}
                </div>
            </Popup>
        </>
    );
});

// Dev Tools
ViewToolbarFacetMenu.displayName = 'ViewToolbarFacetMenu';

export default ViewToolbarFacetMenu;
